/**
 * Road closures, under Plan: the `Planner-Closures` board.
 *
 * A dispatcher records a closure (where, why, from when until when, a note for
 * drivers, and the area as a rectangle), moves its end, or ends it. Spec D5
 * (Ronald, 27 September 2026): the application's only write. Section 6 of the
 * design is the working assumption and not yet confirmed; the questions it
 * leaves open are on the decision sheet, and this screen answers none of
 * them. So the board's "Save and re-plan" is "Record closure" (re-planning is
 * question C8), there is no "Routes affected" (it needs the route check) and
 * no "Import from TomTom" (C12).
 *
 * Every time is typed and shown in the planner's zone, which the list sends
 * as `time_zone`. The area can be drawn on the map and can always be typed as
 * four coordinates, and the list says each closure's area in words, so
 * nothing here needs the map or a pointer.
 *
 * Until bsd-knowledge v0.52 is applied the API refuses every call with
 * CLOSURES_NOT_AVAILABLE, and the screen says that instead of offering a form
 * that cannot be saved.
 */
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  PageHeader,
  Table,
  TextArea,
  TextField,
} from "@depicomnlbv/platform-ui";
import { useEffect, useId, useRef, useState, type FormEvent, type RefObject } from "react";

import {
  errorCode,
  PlannerApiError,
  useChangeClosure,
  useCurrentClosures,
  useImpact,
  useRecordClosure,
  type Closure,
  type ClosureArea,
  type ClosureStatus,
} from "../api";
import {
  areaFields,
  describeArea,
  formatMoment,
  parseArea,
  wallToInstant,
  type AreaFields,
} from "../closureForm";
import { ClosuresMap } from "../components/ClosuresMap";
import { explain, Failure, Pending } from "../components/Failure";
import { formatShortDate, zoneName } from "../time";
import { useTitle } from "../useTitle";

const STATUS: Record<ClosureStatus, { label: string; tone: "critical" | "medium" | "neutral" | "low" }> = {
  active: { label: "Active", tone: "critical" },
  coming: { label: "Coming", tone: "medium" },
  over: { label: "Over", tone: "low" },
  ended: { label: "Ended", tone: "neutral" },
};

/**
 * Why a closure was refused. The closures API words its own refusals of a
 * closure (400, 409 and 422: a period, an area, a change, a copy that went
 * stale) for the person recording it, so that sentence is shown, at its field
 * where it names one. Anything else (a sign-in, a role, a server fault, a
 * request that never got an answer) is said in the application's own words
 * (`explain`), never the API's detail, which is for whoever runs the planner.
 */
function detailOf(error: unknown): string {
  if (error instanceof PlannerApiError && error.detail && [400, 409, 422].includes(error.status)) {
    return error.detail;
  }
  return explain(error).sentence;
}

type FieldName =
  | "name"
  | "valid_from"
  | "valid_to"
  | "review_on"
  | "area"
  | "confirm_farms_inside"
  | keyof ClosureArea;
type Errors = Partial<Record<FieldName, string>>;

const EMPTY_AREA: AreaFields = { south: "", west: "", north: "", east: "" };

/** The value once it has stopped changing for `ms`: the impact is asked of
 *  the area a dispatcher settled on, not of every keystroke towards it. */
function useSettled<T>(value: T, ms = 400): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** `review on 9 Oct 2026`, or nothing the formatter could throw on. */
function reviewText(reviewOn: string | null): string {
  return reviewOn ? `review on ${formatShortDate(reviewOn)}` : "no review date";
}

/** What the map shows of the typed or drawn area: the rectangle as soon as
 *  its four edges are numbers the right way round, and, when it is too large
 *  for TomTom, the reason, so a rectangle drawn too big does not just vanish. */
function drawnArea(fields: AreaFields): { draft: ClosureArea | null; problem: string | null } {
  const parsed = parseArea(fields);
  if ("area" in parsed) return { draft: parsed.area, problem: null };
  const n = (v: string) => Number(v.trim().replace(",", "."));
  const box = { south: n(fields.south), west: n(fields.west), north: n(fields.north), east: n(fields.east) };
  const complete = Object.values(fields).every((v) => v.trim() !== "") && Object.values(box).every(Number.isFinite);
  if (complete && box.north > box.south && box.east > box.west) return { draft: box, problem: parsed.error };
  return { draft: null, problem: null };
}

function NewClosure({
  timeZone,
  area,
  onArea,
}: {
  timeZone: string;
  area: AreaFields;
  onArea: (fields: AreaFields) => void;
}) {
  const record = useRecordClosure();
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [recorded, setRecorded] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  // C5 B: until further notice, with a date to review it.
  const [openEnded, setOpenEnded] = useState(false);
  const [reviewOn, setReviewOn] = useState("");
  // C6 A: the farms the area covers, confirmed by the dispatcher.
  const [confirmed, setConfirmed] = useState(false);
  const areaHint = useId();
  const areaError = useId();
  const coverId = useId();
  const confirmError = useId();
  const parsedArea = parseArea(area);
  const draftArea = "area" in parsedArea ? parsedArea.area : null;
  const settledArea = useSettled(draftArea ? JSON.stringify(draftArea) : null);
  const settledFrom = useSettled(wallToInstant(from, timeZone));
  const impact = useImpact(settledArea ? (JSON.parse(settledArea) as ClosureArea) : null, settledFrom);
  const farms = impact.data?.farms_inside ?? [];
  const bases = impact.data?.bases_inside ?? [];
  // A confirmation is of these farms: a change in which farms are covered
  // asks again.
  const farmKey = farms.map((f) => f.farmer_id).join(",");
  useEffect(() => setConfirmed(false), [farmKey]);

  const form = useRef<HTMLFormElement | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const areaSet = useRef<HTMLFieldSetElement | null>(null);

  // After a refusal, focus goes to the first field to fix (WCAG 3.3.1): the
  // first input marked invalid, in the order the form shows them, or the area
  // when the refusal is about the area as a whole.
  const focusFirst = (found: Errors) => {
    window.setTimeout(() => {
      const input = form.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (input) input.focus();
      else if (found.area) areaSet.current?.focus();
    }, 0);
  };

  const refuse = (found: Errors) => {
    setErrors(found);
    const n = Object.keys(found).length;
    setSummary(`The closure was not recorded: ${n} ${n === 1 ? "field needs" : "fields need"} attention.`);
    focusFirst(found);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // Enter pressed twice would send the closure twice.
    if (record.isPending) return;
    setSummary(null);
    setRecorded(null);
    setRefused(null);
    const found: Errors = {};
    if (!name.trim()) found.name = "Say where the road is closed";
    const validFrom = wallToInstant(from, timeZone);
    const validTo = openEnded ? null : wallToInstant(until, timeZone);
    if (!validFrom) found.valid_from = "From is needed";
    if (!openEnded && !validTo) found.valid_to = "Until is needed";
    if (openEnded && !/^\d{4}-\d{2}-\d{2}$/.test(reviewOn)) found.review_on = "A review date is needed";
    const parsed = parseArea(area);
    if ("error" in parsed) found[parsed.field] = parsed.error;
    if (farms.length > 0 && !confirmed) {
      found.confirm_farms_inside = `Confirm the farms inside the area (${farms
        .map((f) => f.farm_name)
        .join(", ")}), or draw it around them`;
    }
    if (Object.keys(found).length > 0 || "error" in parsed || !validFrom || (!openEnded && !validTo)) {
      refuse(found);
      return;
    }
    setErrors({});

    record.mutate(
      {
        name: name.trim(),
        reason: reason.trim() || null,
        note_for_drivers: note.trim() || null,
        area: parsed.area,
        valid_from: validFrom,
        valid_to: validTo,
        review_on: openEnded ? reviewOn : null,
        confirm_farms_inside: confirmed,
      },
      {
        onSuccess: (closure) => {
          setRecorded(`Recorded: ${closure.name}`);
          setName("");
          setReason("");
          setFrom("");
          setUntil("");
          setNote("");
          setOpenEnded(false);
          setReviewOn("");
          setConfirmed(false);
          onArea(EMPTY_AREA);
        },
        onError: (error) => {
          const field = error instanceof PlannerApiError ? error.field : null;
          if (
            field &&
            ["name", "valid_from", "valid_to", "review_on", "area", "confirm_farms_inside"].includes(field)
          ) {
            refuse({ [field]: detailOf(error) });
          } else {
            setRefused(detailOf(error));
          }
        },
      },
    );
  };

  const corner = (key: keyof ClosureArea, label: string) => (
    <TextField
      errorMessage={errors[key]}
      inputMode="decimal"
      isInvalid={Boolean(errors[key])}
      label={label}
      onChange={(value) => onArea({ ...area, [key]: value })}
      value={area[key]}
    />
  );

  return (
    <section aria-labelledby="new-closure-heading" className="panel">
      <h3 id="new-closure-heading">New closure</h3>
      <form className="closure-form" noValidate onSubmit={submit} ref={form}>
        {summary ? (
          <p className="closure-form__error" role="alert">
            {summary}
          </p>
        ) : null}
        <TextField
          description="The road or the place, as drivers know it."
          errorMessage={errors.name}
          isInvalid={Boolean(errors.name)}
          label="Where"
          onChange={setName}
          value={name}
        />
        <TextField label="Reason" onChange={setReason} value={reason} />
        <div className="closure-form__pair">
          <TextField
            description={`In ${zoneName(timeZone)}.`}
            errorMessage={errors.valid_from}
            isInvalid={Boolean(errors.valid_from)}
            label="From"
            onChange={setFrom}
            type="datetime-local"
            value={from}
          />
          {openEnded ? (
            <TextField
              description="The closure stays in force; from this date it is marked to be checked."
              errorMessage={errors.review_on}
              isInvalid={Boolean(errors.review_on)}
              label="Review on"
              onChange={setReviewOn}
              type="date"
              value={reviewOn}
            />
          ) : (
            <TextField
              errorMessage={errors.valid_to}
              isInvalid={Boolean(errors.valid_to)}
              label="Until"
              onChange={setUntil}
              type="datetime-local"
              value={until}
            />
          )}
        </div>
        <Checkbox isSelected={openEnded} onChange={setOpenEnded}>
          Until further notice
        </Checkbox>
        <TextArea label="Note for drivers" onChange={setNote} rows={3} value={note} />
        <fieldset
          aria-describedby={`${areaHint}${errors.area ? ` ${areaError}` : ""}`}
          className="closure-form__area"
          ref={areaSet}
          tabIndex={-1}
        >
          <legend>Area</legend>
          <p className="closure-form__hint" id={areaHint}>
            A rectangle, at most 160 km a side. Draw it on the map, or type its edges in degrees:
            south and north as latitude, west and east as longitude, west of Greenwich negative.
          </p>
          <div className="closure-form__corners">
            {corner("north", "North")}
            {corner("south", "South")}
            {corner("west", "West")}
            {corner("east", "East")}
          </div>
          {errors.area ? (
            <p className="closure-form__error" id={areaError}>
              {errors.area}
            </p>
          ) : null}
          {/* What the rectangle covers, from the API while it is drawn or
              typed: the factory or a yard is refused (C16), farms are
              confirmed (C6). Said in a live region, so it reaches a reader
              who typed the edges without looking at the map. */}
          <div aria-live="polite" id={coverId}>
            {draftArea && impact.isError ? (
              <p className="closure-form__hint">What this area covers could not be read.</p>
            ) : null}
            {bases.map((b) => (
              <p className="closure-form__error" key={b.location_id}>
                {b.name} lies inside this area. Trips start and end there, so a closure over it
                is refused: draw it on the road itself.
              </p>
            ))}
            {farms.length > 0 ? (
              <p className="closure-form__hint">
                {farms.length} {farms.length === 1 ? "farm lies" : "farms lie"} inside this area:{" "}
                {farms.map((f) => f.farm_name).join(", ")}. A closure blocks them too.
              </p>
            ) : null}
          </div>
          {/* Shown when the area's farms are known, and also when the API
              asked for a confirmation the screen could not foresee (the
              list not read yet, or failed): the API's sentence names them. */}
          {farms.length > 0 || errors.confirm_farms_inside ? (
            <div>
              <Checkbox
                aria-describedby={[errors.confirm_farms_inside ? confirmError : null, coverId]
                  .filter(Boolean)
                  .join(" ")}
                isInvalid={Boolean(errors.confirm_farms_inside)}
                isSelected={confirmed}
                onChange={setConfirmed}
              >
                Record it over these farms
              </Checkbox>
              {errors.confirm_farms_inside ? (
                <p className="closure-form__error" id={confirmError}>
                  {errors.confirm_farms_inside}
                </p>
              ) : null}
            </div>
          ) : null}
        </fieldset>
        <div className="closure-form__actions">
          <Button isPending={record.isPending} pendingLabel="Recording" type="submit">
            Record closure
          </Button>
        </div>
        <div aria-live="polite">
          {recorded ? (
            <p className="closures-status" role="status">
              {recorded}
            </p>
          ) : null}
        </div>
        {refused ? (
          <p className="closure-form__error" role="alert">
            {refused}
          </p>
        ) : null}
      </form>
    </section>
  );
}

function ClosureRow({
  closure,
  timeZone,
  onDone,
}: {
  closure: Closure;
  timeZone: string;
  /** Said once the change is saved; the row may be gone from the list by then. */
  onDone: (message: string) => void;
}) {
  const change = useChangeClosure();
  const [mode, setMode] = useState<"view" | "until" | "end">("view");
  const [until, setUntil] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [openEnded, setOpenEnded] = useState(false);
  const [reviewOn, setReviewOn] = useState("");
  const status = STATUS[closure.status];
  const now = closure.valid_to
    ? formatMoment(closure.valid_to, timeZone)
    : `until further notice, ${reviewText(closure.review_on)}`;
  const open = closure.status === "active" || closure.status === "coming";

  const send = (body: Parameters<typeof change.mutate>[0]["change"], done: string) => {
    // Enter pressed twice would send the change twice, and the second would be
    // refused as made from an old copy.
    if (change.isPending) return;
    setRefused(null);
    change.mutate(
      { closureId: closure.closure_id, change: { ...body, expected_updated_at: closure.updated_at } },
      {
        onSuccess: () => {
          setMode("view");
          onDone(`${done}: ${closure.name}`);
        },
        onError: (e) => {
          // A refusal about the date the dispatcher typed lands at that field.
          const field = e instanceof PlannerApiError ? e.field : null;
          if (field === "valid_to" || field === "review_on") setError(detailOf(e));
          else setRefused(detailOf(e));
        },
      },
    );
  };

  const saveUntil = (event: FormEvent) => {
    event.preventDefault();
    if (openEnded) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(reviewOn)) {
        setError("A review date is needed");
        return;
      }
      setError(null);
      send({ valid_to: null, review_on: reviewOn }, "Until further notice");
      return;
    }
    const validTo = wallToInstant(until, timeZone);
    if (!validTo) {
      setError("Until is needed");
      return;
    }
    setError(null);
    send({ valid_to: validTo }, "Until moved");
  };

  return (
    <>
      <tr>
        <th scope="row">
          {closure.name}
          <p className="closure-row__detail">
            {describeArea(closure.area)}
            {closure.is_rectangle ? "" : ". Not a rectangle: these are its bounds"}
          </p>
          {closure.note_for_drivers ? (
            <p className="closure-row__detail">Note for drivers: {closure.note_for_drivers}</p>
          ) : null}
          <p className="closure-row__detail">
            Recorded by {closure.recorded_by ?? "unknown"}
            {closure.ended_by ? `, ended by ${closure.ended_by}` : ""}
          </p>
        </th>
        <td>{closure.reason ?? "No reason given"}</td>
        <td className="tabular">{formatMoment(closure.valid_from, timeZone)}</td>
        <td className="tabular">
          {closure.valid_to ? (
            formatMoment(closure.valid_to, timeZone)
          ) : (
            <>
              Until further notice
              <p className="closure-row__detail">{reviewText(closure.review_on)}</p>
            </>
          )}
        </td>
        <td>
          <div className="closure-row__badges">
            <Badge tone={status.tone}>{status.label}</Badge>
            {/* V-C5 A: from its review date, for every dispatcher. In words,
                not colour alone, and it stays in force. */}
            {closure.to_be_checked ? <Badge tone="high">To be checked</Badge> : null}
          </div>
        </td>
        <td>
          {open ? (
            <div className="closure-row__actions">
              <Button
                onPress={() => {
                  setUntil("");
                  setOpenEnded(closure.valid_to === null);
                  setReviewOn(closure.review_on ?? "");
                  setError(null);
                  setMode("until");
                }}
                variant="secondary"
              >
                Change until
              </Button>
              <Button onPress={() => setMode("end")} variant="secondary">
                End now
              </Button>
            </div>
          ) : null}
        </td>
      </tr>
      {mode !== "view" ? (
        <tr className="closure-row__edit">
          <td colSpan={6}>
            {mode === "until" ? (
              <form className="closure-row__inline" noValidate onSubmit={saveUntil}>
                <Checkbox
                  isSelected={openEnded}
                  onChange={(on) => {
                    setOpenEnded(on);
                    setError(null);
                    setRefused(null);
                  }}
                >
                  {`Until further notice: ${closure.name}`}
                </Checkbox>
                {openEnded ? (
                  <TextField
                    description={`Now ${now}. It stays in force; from this date it is marked to be checked.`}
                    errorMessage={error ?? undefined}
                    isInvalid={Boolean(error)}
                    label={`New review date for ${closure.name}`}
                    onChange={setReviewOn}
                    type="date"
                    value={reviewOn}
                  />
                ) : (
                  <TextField
                    description={`In ${zoneName(timeZone)}. Now ${now}.`}
                    errorMessage={error ?? undefined}
                    isInvalid={Boolean(error)}
                    label={`New until for ${closure.name}`}
                    onChange={setUntil}
                    type="datetime-local"
                    value={until}
                  />
                )}
                <div className="closure-row__actions">
                  <Button isPending={change.isPending} pendingLabel="Saving" type="submit">
                    Save until
                  </Button>
                  <Button onPress={() => setMode("view")} variant="subtle">
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="closure-row__inline">
                <p>
                  End {closure.name} now? An ended closure cannot be changed again; a new closure
                  can be recorded for the same road.
                </p>
                <div className="closure-row__actions">
                  <Button
                    isPending={change.isPending}
                    onPress={() => send({ end: true }, "Ended")}
                    pendingLabel="Ending"
                  >
                    End closure
                  </Button>
                  <Button onPress={() => setMode("view")} variant="subtle">
                    Keep it
                  </Button>
                </div>
              </div>
            )}
            {refused ? (
              <p className="closure-form__error" role="alert">
                {refused}
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function Closures() {
  useTitle("Road closures");
  const closures = useCurrentClosures();
  const [area, setArea] = useState<AreaFields>(EMPTY_AREA);
  const [done, setDone] = useState<string | null>(null);
  const listHeading = useRef<HTMLHeadingElement | null>(null);
  const { draft, problem } = drawnArea(area);

  // After a change the row may leave the list (an ended closure is not
  // "active or coming"), taking the focused button with it. So the result is
  // said in the status region and focus goes to the list's heading.
  const onDone = (message: string) => {
    setDone(message);
    window.setTimeout(() => listHeading.current?.focus(), 0);
  };

  let body;
  if (closures.isPending) {
    body = <Pending>Reading the road closures</Pending>;
  } else if (closures.isError && errorCode(closures.error) === "CLOSURES_NOT_AVAILABLE") {
    body = (
      <section className="panel">
        <p>
          Road closures cannot be recorded yet. The database change they need has not been
          applied; until it is, the planner plans without closures.
        </p>
      </section>
    );
  } else if (closures.isError) {
    body = <Failure error={closures.error} />;
  } else {
    const { time_zone: timeZone, closures: rows } = closures.data;
    body = (
      <div className="closures-layout">
        <ClosuresMap
          closures={rows}
          draft={draft}
          draftProblem={problem}
          onDrawn={(drawn) => setArea(areaFields(drawn))}
        />
        <div className="closures-side">
          <NewClosure area={area} onArea={setArea} timeZone={timeZone} />
          <section aria-labelledby="closures-list-heading" className="panel">
            <h3 id="closures-list-heading" ref={listHeading as RefObject<HTMLHeadingElement>} tabIndex={-1}>
              Active and coming
            </h3>
            <div aria-live="polite">
              {done ? (
                <p className="closures-status" role="status">
                  {done}
                </p>
              ) : null}
            </div>
            {rows.length === 0 ? (
              <EmptyState>No road closures are active or coming.</EmptyState>
            ) : (
              // The Table block is the scroll box: the six columns scroll in
              // it on a phone, and the page does not (WCAG 1.4.10).
              <Table caption="Active and coming road closures, earliest first">
                <thead>
                  <tr>
                    <th scope="col">Where</th>
                    <th scope="col">Reason</th>
                    <th scope="col">From</th>
                    <th scope="col">Until</th>
                    <th scope="col">Status</th>
                    <th className="closures-actions-head" scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((closure) => (
                    <ClosureRow
                      closure={closure}
                      key={closure.closure_id}
                      onDone={onDone}
                      timeZone={timeZone}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        qualifier="Roads the planner avoids for a while. A closure is a rectangle on the map, from a time until a time."
        title="Road closures"
      />
      {body}
    </>
  );
}
